import { useState, useEffect } from "react";
import moment from "moment";
import { setInitialFilterConf } from '../../slices/filters/thunk'
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EditFilled, DownloadOutlined } from "@ant-design/icons";

// Redux
import { useDispatch } from "react-redux";

// Ant Design Components
import {
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Button,
  Typography,
  Spin,
  Table,
  Checkbox,
  Tabs,
  Tooltip,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { loadAccounts } from '../../slices/thunk';

// Breadcrumb & Styles
import Breadcrumb from "../../Common/Breadcrumb";
import withRouter from "../../Common/withRouter";

// Redux actions
import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();
const { Text } = Typography;

const userLvlTitleMap = {
  'LIMITED_ORG_ACCESS': 'Omejen',
  'FULL_ORG_ACCESS': 'Poln',
}

const OrgSettings = () => {
  document.title = "Nastavitve organizacije" + process.env.REACT_APP_PAGE_TITLE;

  const dispatch = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(location.hash ? location.hash.substring(1) : "accounts");

  const [form] = Form.useForm();
  const [orgSettings, setOrgSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupLogsLoading, setBackupLogsLoading] = useState(false);

  useEffect(() => {
    // No filters shown
    dispatch(setInitialFilterConf({ data: {}, hidden: "*" }))
  }, []);

  useEffect(() => {
    api.get(`/api/accounts/`, {}).then((resp) => {
      const data = {
        accounts: (resp || []).map((acc) => ({
          id: acc.id,
          title: acc.title || "",
          balance: acc.balance || 0,
          balance_date: acc.balance_date
            ? moment(acc.balance_date, "YYYY-MM-DD")
            : null,
          is_default: acc.is_default || false,
        })),
      };
      form.setFieldsValue(data);
      setOrgSettings(data);
      setLoading(false);
    });

  }, [form]);

  useEffect(() => {
    setUsersLoading(true);
    api.get(`/api/organization/users/`, {})
      .then((resp) => setUsers(resp))
      .finally(() => setUsersLoading(false));
  }, []);

  const loadBackupLogs = () => {
    setBackupLogsLoading(true);
    api.get('/api/backups/')
      .then(res => setBackupLogs(res?.results ?? res ?? []))
      .catch(err => console.error('Backup logs fetch error:', err))
      .finally(() => setBackupLogsLoading(false));
  };

  useEffect(() => { loadBackupLogs(); }, []);

  const handleBackupDownload = () => {
    setBackupDownloading(true);
    api.get('/api/backups/download/')
      .then(res => {
        if (!res) return;
        const a = document.createElement('a');
        a.href = res.url;
        a.download = res.filename;
        a.click();
        loadBackupLogs();
      })
      .catch(err => console.error('Backup download error:', err))
      .finally(() => setBackupDownloading(false));
  };

  useEffect(() => {
    setActiveTab(location.hash ? location.hash.substring(1) : "accounts");
  }, [location.hash]);

  const onFinish = (values) => {
    setSubmitting(true);
    const payload = {
      ...values,
      accounts: values.accounts?.map((acc) => ({
        ...acc,
        balance_date: acc.balance_date
          ? acc.balance_date.format("YYYY-MM-DD")
          : null,
      })),
    };

    api
      .create(`/api/accounts/update_accounts/`, payload, {successMsg: `Računi so bili posodobljeni!`})
      .then(() => {
        dispatch(loadAccounts());
      })
      .finally(() => setSubmitting(false));
  };

  const handleDefaultChange = (changedIndex) => {
    const accounts = form.getFieldValue("accounts") || [];
    const updated = accounts.map((acc, idx) => ({
      ...acc,
      is_default: idx === changedIndex,
    }));
    form.setFieldsValue({ accounts: updated });
  };

  if (loading) {
    return (
      <Spin size="large" style={{ display: "block", margin: "100px auto" }} />
    );
  }

  const userTableColumns = [
    {
      title: "Uporabniško ime",
      dataIndex: "username",
      key: "username",
      render: x => <b>{x}</b>
    },
    {
      title: "E-pošta",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Raven dostopa",
      dataIndex: "access_level",
      key: "access_level",
      render: (val) => userLvlTitleMap[val]
    },
    {
      title: "Uredi",
      key: "edit",
      render: (record) => (
        <Link to={`/users/${record.id}`}>
          <Button size="small"><EditFilled /></Button>
        </Link>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb parentTitle="Konfiguracija" pageTitle="Nastavitve organizacije" />

      <div style={{ marginTop: "24px" }}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => { setActiveTab(key); navigate(`#${key}`, { replace: true }); }}
          items={[
            {
              key: "accounts",
              label: "Transakcijski računi",
              children: (
                <Card title="Transakcijski računi">
                  <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.List name="accounts">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <Card
                              key={key}
                              type="inner"
                              style={{ marginBottom: 16 }}
                              title={
                                form.getFieldValue(["accounts", name, "title"])?.trim()
                                  ? form.getFieldValue(["accounts", name, "title"])
                                  : `Račun ${name + 1}`
                              }
                              extra={
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                />
                              }
                            >
                              {/* Hidden ID field */}
                              <Form.Item {...restField} name={[name, "id"]} hidden>
                                <Input type="hidden" />
                              </Form.Item>

                              <Row gutter={16}>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "title"]}
                                    label="Naziv računa"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Vnesite naziv računa",
                                      },
                                    ]}
                                  >
                                    <Input placeholder="npr. Transakcijski račun" />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "balance"]}
                                    label="Stanje (€)"
                                    rules={[
                                      { required: true, message: "Vnesite stanje" },
                                    ]}
                                  >
                                    <InputNumber
                                      style={{ width: "100%" }}
                                      addonAfter="€"
                                      formatter={(value) =>
                                        value
                                          ? Number(value).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                          : ""
                                      }
                                      parser={(value) =>
                                        value ? value.replace(/,/g, "") : ""
                                      }
                                      precision={2}
                                      step={0.01}
                                      min={0}
                                      placeholder="Vnesite stanje"
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "balance_date"]}
                                    label="Datum stanja"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Izberite datum stanja",
                                      },
                                    ]}
                                  >
                                    <DatePicker
                                      format="DD.MM.YYYY"
                                      style={{ width: "100%" }}
                                      getPopupContainer={(trigger) =>
                                        trigger.parentElement
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>

                              <Row gutter={16}>
                                <Col span={8}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "is_default"]}
                                    valuePropName="checked"
                                  >
                                    <Checkbox
                                      onChange={() => handleDefaultChange(index)}
                                    >
                                      Privzeti račun
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              block
                              icon={<PlusOutlined />}
                            >
                              Dodaj račun
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>

                    <div className="text-center" style={{ marginTop: 30 }}>
                      <Button type="primary" htmlType="submit" loading={submitting}>
                        Shrani
                      </Button>
                    </div>
                  </Form>
                </Card>
              ),
            },
            {
              key: "users",
              label: "Uporabniki",
              children: (
                <Card
                  title="Uporabniki"
                  extra={
                    <Tooltip title="Ustvari novega uporabnika!">
                      <Link to="/create_user">
                        <Button icon={<PlusOutlined />} />
                      </Link>
                    </Tooltip>
                  }
                >
                  <Table
                    loading={usersLoading}
                    dataSource={(users || []).map((u) => ({ ...u, key: `user-${u.id}` }))}
                    columns={userTableColumns}
                  />
                </Card>
              ),
            },
            {
              key: "backups",
              label: "Varnostne kopije",
              children: (
                <Card loading={backupLogsLoading} title="Varnostne kopije">
                  <Button icon={<DownloadOutlined />} loading={backupDownloading} onClick={handleBackupDownload} style={{ marginBottom: 16 }}>
                    Prenesi zadnjo kopijo
                  </Button>
                  <hr />
                  <h6>Zadnji prenosi</h6>
                  {backupLogs.map(log => (
                    <div key={log.id}>
                      {log.ago} — {log.username}
                    </div>
                  ))}
                </Card>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default withRouter(OrgSettings);
